import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useStore, useDispatch } from 'react-redux';
import {
  AppBar,
  Grid,
  Box,
  Snackbar,
  IconButton,
  Toolbar,
  Typography,
  Collapse
} from '@material-ui/core';
import EmailEditor from 'react-email-editor';
import TemplateKeyBank from 'components/Template/TemplateKeyBank';
import PageLayout from 'components/Template/PageLayout';
import get from 'lodash.get';
import { Code, Queue } from '@material-ui/icons';
import Alert from 'components/Alert';
import UploadsPanel from 'components/Template/UploadsPanel';
import sessionHelper from 'helpers/sessionHelper';
import filterDataFrame from './filterDataFrame';
import formSurveyKeys from './formSurveyKeys';
import { ProductType } from 'constants.js';
import { getEnvVar } from 'helpers';
import { UnlayerProjectIds } from 'constants.js';
import { isArrayValuesEqual, isArrayLengthEqual, sanitizeData, sanitizeHtml, sanitizeHtmlWithHandlebarPreservation } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../../../views/Settings/useLanguageSettings';
import axios from 'axios';
import { actions as uploadActions } from 'store/uploads';
import PopupModal from 'views/UserMessaging/customModalContent/popupModal';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  iconButtonLabel: {
    display: 'flex',
    flexDirection: 'column'
  },
  editorWrapper: {
    position: 'relative',
    paddingTop: '0px !important'
  },
  templateKeysPanel: {
    backgroundColor: 'rgb(249, 249, 249)',
    height: '507px'
  },
  sideKeysPanel: {
    backgroundColor: 'rgb(249, 249, 249)',
    height: '507px',
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  toolContainer: {
    position: 'absolute',
    right: 0,
    top: -6,
    height: '507px',
    marginRight: '10px',
    paddingTop: '6px'
  },
  overlayToolContainer: {
    position: 'absolute',
    right: 0,
    height: '507px',
    marginRight: '10px',
    paddingTop: '6px',
    backgroundColor: '#fff'
  },
  editorContainer: {
    position: 'absolute',
    left: 98,
    top: -6,
    height: '489px',
    marginRight: '10px',
    paddingTop: '6px'
  },
  previewEditorContainer: {
    position: 'absolute',
    top: -6,
    height: '450px',
    marginRight: '10px',
    paddingTop: '6px'
  },
  tools: {
    padding: '0px'
  },
  subjectLine: {
    flex: '1 1 0%',
    paddingRight: theme.spacing(4)
  },
  editorBar: {
    margin: '0px 10px'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-around'
  }
}));

const unsubscribeTemplateKey = (label, placeholder) => {
  return {
    dataType: 'String',
    key: 'unsubscribeLink',
    name: label,
    text: `{{{unsubscribeLink text="${placeholder}"}}}`
  };
};

const dateTimeNowTemplateKey = (label, key) => {
  return {
    dataType: 'String',
    key: key,
    name: label,
    text: `{{{${key}}}}`
  };
};

const surveyDataFrame = {
  key: 'surveys',
  name: 'Surveys',
  text: '{{surveys}}',
  attributeFieldType: 'dataFrame',
  subAttribute: {
    size: 2,
    attributes: [
      {
        key: 'surveyQuestion',
        name: 'Survey Question',
        dataType: 'String',
        text: '{{surveyQuestion}}'
      },
      {
        key: 'surveyResponse',
        name: 'Survey Response',
        dataType: 'String',
        text: '{{surveyResponse}}'
      }
    ]
  }
};

const MISSING_UNSUBSCRIBE_LINK_ERROR_MESSAGE =
  'Unable to save the template because you have not added an unsubscribe template key';
const MISSING_UNSUBSCRIBE_LINK_ERROR_MESSAGE_FOR_TEST =
  'Unable to test the template because you have not added an unsubscribe template key';

const TOOLBAR_SMALL_WIDTH = 425;
const TOOLBAR_LARGE_WIDTH = 426;

const EDITOR_WIDTH_BREAKPOINT_LARGE = 1366;
const EDITOR_WIDTH_BREAKPOINT_SMALL = 1031;
const TEMPLATE_EDITOR_WIDTH = 661;

const EDITOR_LARGE_WIDTH = 1030;
const PREVIEW_EDITOR_SMALL_WIDTH = 100;
const PREVIEW_EDITOR_LARGE_WIDTH = 87;
const PREVIEW_EDITOR_WIDTH = 112;
const SCROLLBAR_WIDTH = 35;
const IMAGE_FILE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];

const templateKeys = (
  templateKeys,
  useUnsubscribeLinkOption,
  useSurveysLinkOption,
  surveys,
  thisTemplate,
  editorStaticContentValues
) => {
  let tempKeys = templateKeys;
  tempKeys = tempKeys.map(keys => {
    let schemaConditionAttributes =
      keys.schemaConditionAttributes &&
      keys.schemaConditionAttributes.filter(
        i => i.attributeFieldType !== 'dataFrame'
      );
    if ((keys.fieldFromSchema || keys.schemaType === "EVENT") || (thisTemplate.productType === ProductType.GUEST_INSIGHTS && keys.schemaType === "GUEST_INSIGHT")) {
      schemaConditionAttributes = useUnsubscribeLinkOption
        ? [
          ...schemaConditionAttributes,
          unsubscribeTemplateKey(
            editorStaticContentValues?.unsubscribeLabel || `Unsubscribe Link`,
            editorStaticContentValues?.unsubscribePlaceholder || `Click here to unsubscribe`
          ),
          dateTimeNowTemplateKey(
            editorStaticContentValues?.dateTimeNowLabel || `Date Time Now`,
            editorStaticContentValues?.dateTimeNowKey || `dateTimeNow`
          )
        ]
        : [...schemaConditionAttributes, 
          dateTimeNowTemplateKey(
            editorStaticContentValues?.dateTimeNowLabel || `Date Time Now`,
            editorStaticContentValues?.dateTimeNowKey || `dateTimeNow`
          )
        ];
      if (surveys?.length > 0 && useSurveysLinkOption) {
        let tempSurveys = formSurveyKeys(surveys, thisTemplate, editorStaticContentValues?.surveysLabel || `Surveys`);
        if (tempSurveys?.surveyMenuOptions?.length > 0) {
          schemaConditionAttributes = [
            ...schemaConditionAttributes,
            tempSurveys
          ];
        }
      }
    }
    return { ...keys, schemaConditionAttributes: schemaConditionAttributes.sort((a, b) => a?.name.localeCompare(b?.name)) };
  });
  return tempKeys;
};

function calculateWidths(
  width
) {
  if (width > EDITOR_WIDTH_BREAKPOINT_LARGE) {
    return {
      toolBarWidth: TOOLBAR_LARGE_WIDTH,
      previewEditorWidth: PREVIEW_EDITOR_LARGE_WIDTH,
      editorWidth:
        width -
        (TOOLBAR_LARGE_WIDTH + PREVIEW_EDITOR_LARGE_WIDTH + SCROLLBAR_WIDTH)
    };
  }
  if (
    width < EDITOR_WIDTH_BREAKPOINT_LARGE &&
    width > EDITOR_WIDTH_BREAKPOINT_SMALL
  ) {
    return {
      toolBarWidth: TOOLBAR_SMALL_WIDTH,
      previewEditorWidth: PREVIEW_EDITOR_SMALL_WIDTH,
      editorWidth:
        width -
        (TOOLBAR_SMALL_WIDTH + PREVIEW_EDITOR_SMALL_WIDTH + SCROLLBAR_WIDTH)
    };
  }

  if (width < EDITOR_WIDTH_BREAKPOINT_SMALL) {
    let toolbarWidth = width - TEMPLATE_EDITOR_WIDTH;
    let previewEditorWidth = width - PREVIEW_EDITOR_WIDTH;
    return {
      toolBarWidth: toolbarWidth,
      previewEditorWidth: previewEditorWidth,
      editorWidth:
        width - (toolbarWidth + previewEditorWidth + SCROLLBAR_WIDTH)
    };
  }

  return {};
}

function updateValidationError(
  data,
  setIsValidationError,
  refreshSessionIfNeeded
) {
  let type = data.type;
  let item = data.item;
  let changes = data.changes;

  if (data?.item?.type === 'custom') {
    if (type === 'content:added') {
      let updatedValidation = {};
      updatedValidation[item.id] = {isDisabled: true};
      if (item.slug === 'DataFrame' && item.values.menu.item?.dataFrameName &&
        (item.values.menu.item[item.values.menu.item.dataFrameName]).length > 0
      ) {
        updatedValidation[item.id] = {isDisabled: false};
      }
      if (
        (item.slug === 'QrCode') &&
        item.values.menu.item?.key &&
        (item.values.menu.item.isHeightDirty ||
          item.values.menu.item.isWidthDirty || 
          item.values.menu.item.isKeyDirty ||
          item.values.menu.item.isInvalidLength)
      ) {
        updatedValidation[item.id] = {isDisabled: true};
      }
      if (
        (item.slug === 'Barcode') &&
        item.values.menu.item?.key &&
        (item.values.menu.item.isHeightDirty ||
          item.values.menu.item.isWidthDirty || 
          item.values.menu.item.isKeyDirty ||
          item.values.menu.item.isInvalidLength)
      ) {
        updatedValidation[item.id] = {isDisabled: true};
      }
      if (item.slug === 'Iteration') {
        if (
          !item.values.menu.item.dataFrameName || !item.values.menu.item.iterationType
        ) {
          updatedValidation[item.id] = {isDisabled: true};
        } else {
          updatedValidation[item.id] = {isDisabled: false};
        }
      }
      setIsValidationError(prevState => ({
        ...prevState,
        ...updatedValidation
      }));
    }
    if (type === 'content:removed') {
      setIsValidationError(prevState => {
        delete prevState[item.id];
        return { ...prevState };
      });
    }
    if (type === 'content:modified') {
      // Only validate if this is a custom property change (changes.value.item exists)
      // Skip validation for style/editor property changes
      if (!changes.value?.item) {
        // Don't update validation for non-custom property changes, but continue to refreshSession
        refreshSessionIfNeeded();
        return;
      }
      
      let isDirty = true; // Keep original behavior - start with true
      
      if (item.slug === 'DataFrame') {
        if(changes.value.item?.dataFrameName && (changes.value.item[changes.value.item.dataFrameName])?.length > 0) {
          isDirty = false; // Keep original logic
        }
      }
      if (item.slug === 'QrCode') {
        if(changes.value.item) {
          isDirty =
            !changes.value.item.key ||
            changes.value.item?.isHeightDirty ||
            changes.value.item?.isWidthDirty ||
            changes.value.item?.isKeyDirty ||
            changes.value.item?.isInvalidLength;
        }
      }
      if (item.slug === 'Barcode') {
        if(changes.value.item) {
          isDirty =
            !changes.value.item.key ||
            changes.value.item?.isHeightDirty ||
            changes.value.item?.isWidthDirty ||
            changes.value.item?.isKeyDirty ||
            changes.value.item?.isInvalidLength;
        }
      }
      if (item.slug === 'Iteration') {
        if (!changes.value.item.dataFrameName || !changes.value.item.iterationType) {
          isDirty = true;
        } else {
          isDirty = false;
        }
      }
      if (isDirty?.length > 0 || isDirty === true) {
        let updatedValidation = {};
        updatedValidation[item.id] = {isDisabled: true};
        setIsValidationError(prevState => ({
          ...prevState,
          ...updatedValidation
        }));
      } else {
        let updatedValidation = {};
        updatedValidation[item.id] = {isDisabled: false};
        setIsValidationError(prevState => ({
          ...prevState,
          ...updatedValidation
        }));
      }
    }
  }

  refreshSessionIfNeeded();
}

const validateIteration = (current, setIsValidationError) => {

  current.exportHtml(data => {
    // Fetch all components from rows -> columns -> contents
    const allComponents = data.design.body.rows
      .map(row => row.columns
        .map(column => column.contents))
          .flat(2);

    let startComponents = [];
    let endComponents = [];
    let nestedComponents = [];
    let stack = [];

    allComponents.forEach(content => {
      if (content?.slug === 'Iteration' && content.values.menu.item.key) {
        if (content.values.menu.item.iterationType === 'startIteration') {
          startComponents.push(content.values.menu.item.key);
          stack.push(content.values.menu.item.key);
        }
        if (content.values.menu.item.iterationType === 'endIteration') {
          endComponents.push(content.values.menu.item.key);
          if (stack.length > 0) {
            let startKey = stack.pop();
            let endKey = content.values.menu.item.key;
            if (startKey === endKey) {
              nestedComponents.push({ start: startKey, end: content.values.menu.item.key });
            }
          }
        }
      }
    });
    if (!isArrayValuesEqual(startComponents, endComponents) || (!isArrayLengthEqual(startComponents, endComponents, nestedComponents))) {
      setIsValidationError(prevState => ({
        ...prevState,
        ['iterationValidation']: { isDisabled: true }
      }));
    } else {
      setIsValidationError(prevState => {
        delete prevState['iterationValidation'];
        return { ...prevState };
      });
    }
  });
};

const isSurveyLinkIncluded = data =>
  data.html.includes('{{surveyRecipientIdentifier}}');

const isTemplateMissingUnsubscribeLink = (data) =>
  !data.html.includes('{{{unsubscribeLink text=');

function updateSubscribe(
  event,
  thisTemplate,
  data,
  useUnsubscribeLinkOption,
  openSnackBarError,
  editorStaticContentValues
) {
  let missingLink = 0;
  const hasCampaignOccurrenceType = Boolean(thisTemplate?.campaignOccurrenceType);
  const isPromotionalCampaign = thisTemplate?.promotional === true;
  if (
    event?.type !== 'autoSaveTemplate' &&
    isPromotionalCampaign &&
    (!hasCampaignOccurrenceType ||
      (thisTemplate.campaignOccurrenceType !== 'ON_EVENT' &&
        thisTemplate.campaignOccurrenceType !== 'SCHEDULED'))
  ) {
    if (useUnsubscribeLinkOption && isTemplateMissingUnsubscribeLink(data)) {
      openSnackBarError('error', event?.type === 'testTemplate' ? (editorStaticContentValues?.unsubscribeErrorMsgForTest || MISSING_UNSUBSCRIBE_LINK_ERROR_MESSAGE_FOR_TEST) : (editorStaticContentValues?.unsubscribeErrorMsg || MISSING_UNSUBSCRIBE_LINK_ERROR_MESSAGE));
      missingLink = 1;
    }
  }
  return missingLink;
}

function testEventType(event, validateTestTemplate, setTriggerSave) {
  if (event?.type !== 'customizeTemplate') {
    event?.type === 'testTemplate'
      ? validateTestTemplate()
      : setTriggerSave(true);
  }
}

function triggerSaveOnCondition(
  triggerSave,
  shouldAutoSave,
  autoSaveTemplate,
  saveTemplate,
  setTriggerSave
) {
  if (triggerSave) {
    shouldAutoSave ? autoSaveTemplate() : saveTemplate();
    setTriggerSave(false);
  }
}
// date formats here - https://docs.oracle.com/javase/8/docs/api/java/text/SimpleDateFormat.html
function dateFormatFunction(settingsMetaDataStore, editorStaticContentValues) {
  return [
    { value: 'MM/dd/yyyy', displayExample: '04/19/2021' },
    { value: 'MM/dd/yy', displayExample: '04/19/21' },
    { value: 'MM/dd', displayExample: '04/19' },

    { value: 'dd/MM/yyyy', displayExample: '19/04/2021' },
    { value: 'dd/MM/yy', displayExample: '19/04/21' },
    { value: 'dd/MM', displayExample: '19/04/' },

    { value: 'EEEE, MMMM d, yyyy', displayExample: `${editorStaticContentValues?.mondayLabel || `Monday`}, ${editorStaticContentValues?.aprilLabel || `April`} 21, 2021` },
    { value: 'EEEE, d MMMM, yyyy', displayExample: `${editorStaticContentValues?.mondayLabel || `Monday`}, 21 ${editorStaticContentValues?.aprilLabel || `April`}, 2021` },

    { value: 'MMMM d', displayExample: `${editorStaticContentValues?.aprilLabel || `April`} 21` },
    { value: 'MMMM d, yyyy', displayExample: `${editorStaticContentValues?.aprilLabel || `April`} 21, 2021` },
    { value: 'd MMMM, yyyy', displayExample: `21 ${editorStaticContentValues?.aprilLabel || `April`}, 2021` },
    {
      value: 'MMM dd, yyyy hh:mm aaa',
      displayExample: `${editorStaticContentValues?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'MMM dd, yyyy hh:mm aaa',
      displayExample: `${editorStaticContentValues?.janLabel || `Jan`} 17, 2023 01:30 PM (${editorStaticContentValues?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    {
      value: 'EEEE, MMM dd, yyyy hh:mm aaa',
      displayExample: `${editorStaticContentValues?.mondayLabel || `Monday`}, ${editorStaticContentValues?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'EEEE, MMM dd, yyyy hh:mm aaa',
      displayExample: `${editorStaticContentValues?.mondayLabel || `Monday`}, ${editorStaticContentValues?.janLabel || `Jan`} 17, 2023 01:30 PM (${editorStaticContentValues?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    { value: 'hh:mm aaa', displayExample: '01:30 PM' },
    {
      value: 'hh:mm aaa',
      displayExample: `01:30 PM (${editorStaticContentValues?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    }
  ];
}

const alignmentOptions = [
  {
    value: 'None'
  },
  {
    value: 'Left'
  },
  {
    value: 'Center'
  },
  {
    value: 'Right'
  }
];

const acceptTypes = (getEnvVar(`ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS`) || `image/jpeg,image/png,image/jpg`).split(',');
const acceptTypesLabel = getEnvVar(`ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL`) || `JPEG, PNG and JPG`;
const acceptLimit = Number(getEnvVar(`ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB`) || 10) * 1024 * 1024;
const acceptLimitLabel = getEnvVar(`ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB`) || `10`;
const imagePlaceholder = getEnvVar(`ADM_IMAGE_PLACEHOLDER`) || `https://cdn.tools.unlayer.com/image/placeholder.png`;

function getData(dataFrameKeys, dateFormatMenuOptions, openSnackBarError, editorStaticContent, setCurrentCopiedValue, hasMultipleItinerary) {
  return {
    dataFrameKeys: dataFrameKeys || [],
    frames: dataFrameKeys.namings || [],
    dataFields: getDataField(dataFrameKeys) || [],
    nestedDataFields: getNestedDataField(dataFrameKeys) || [],
    dateFormatMenuOptions: dateFormatMenuOptions || [],
    alignmentOptions: alignmentOptions || [],
    openSnackBarError: openSnackBarError,
    changeCurrentCopiedValue: setCurrentCopiedValue,
    decimalFormatTypes: (getEnvVar('ADM_DECIMAL_FORMAT_TYPES') || '').split(',') || [],
    contentServiceData: {
      apiBase: window.origin || '',
      tenantId: window.localStorage.getItem('GCM-tenantId') || '',
      propertyId: window.localStorage.getItem('GCM-businessContextId') || 'Default',
      xToken: window.localStorage.getItem('GCM-xToken') || '',
      customTag: (window._env_ && window._env_.ADM_UPLOAD_DEFAULT_TAG) || 'adm',
      acceptTypes: acceptTypes,
      acceptTypesLabel: acceptTypesLabel,
      acceptLimit: acceptLimit,
      acceptLimitLabel: acceptLimitLabel
    },
    translations: editorStaticContent,
    hasMultipleItinerary: hasMultipleItinerary || false,
  };
}

function getDataField(dataFrameKeys) {
  let dataFrameArray = dataFrameKeys.detailedList.map(list => {
    return {
      ...list,
      dataFrames: list.dataFrames.filter(frame => !frame.hasNestedDataFrame && !frame.metaAttribute) || []
    }
  });
  return dataFrameArray;
}

function getNestedDataField(dataFrameKeys) {
  let nestedDataFrameArray = dataFrameKeys.detailedList.map(list => {
    return {
      ...list,
      dataFrames: list.dataFrames.filter(frame => frame.hasNestedDataFrame && !frame.metaAttribute) || []
    }
  });
  return nestedDataFrameArray;
}

const getAssetImageLocation = asset => String(asset?.cdnPublicUrl || asset?.publicUrl || '').trim();

const isImageAsset = asset => {
  const imageUrl = getAssetImageLocation(asset).toLowerCase();
  if (!imageUrl) {
    return false;
  }
  const contentType = String(
    asset?.contentMimeType ||
      asset?.contentType ||
      asset?.mimeType ||
      asset?.type ||
      ''
  ).toLowerCase();
  if (contentType.startsWith('image/')) {
    return true;
  }
  return IMAGE_FILE_EXTENSIONS.some(ext => imageUrl.includes(ext));
};

const mapAssetToUnlayerUpload = asset => {
  const location = getAssetImageLocation(asset);
  if (!location) {
    return null;
  }

  const width = Number(asset?.width || 0) || 9999;
  const height = Number(asset?.height || 0) || 9999;

  return {
    id: String(asset?.id || asset?.assetId || asset?.name || asset?.fileName || location),
    location,
    width,
    height,
    contentType:
      asset?.contentMimeType ||
      asset?.contentType ||
      asset?.mimeType ||
      asset?.type ||
      'image/jpeg',
    source: 'user',
    size: Number(asset?.fileSize || 0) || undefined
  };
};

const getUnlayerUploadsResponse = (assets = [], params = {}) => {
  const page = Number(params?.page || 1) || 1;
  const perPage = Number(params?.perPage || 20) || 20;
  const searchText = String(params?.searchText || '').trim().toLowerCase();

  const images = (assets || [])
    .filter(isImageAsset)
    .filter(asset => {
      if (!searchText) {
        return true;
      }
      const fileName = String(asset?.fileName || asset?.name || '').toLowerCase();
      const url = getAssetImageLocation(asset).toLowerCase();
      return fileName.includes(searchText) || url.includes(searchText);
    })
    .map(mapAssetToUnlayerUpload)
    .filter(Boolean);

  const total = images.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;

  return {
    images: images.slice(startIndex, endIndex),
    pagination: {
      page,
      perPage,
      total,
      hasMore: endIndex < total
    }
  };
};

const TemplateEditor = forwardRef((props, ref) => {
  const {
    thisTemplate,
    useUnsubscribeLinkOption,
    useSurveysLinkOption,
    handleChange,
    surveys,
    onFocus,
    onBlur,
    saveTemplate,
    children,
    validateTestTemplate,
    shouldAutoSave,
    autoSaveTemplate,
    isTemplateReadOnly,
    isPopupDialog,
    setIsValidationError,
    modifyPageIterator,
    bypassLanguageCheck = false
  } = props;

  const hasMultipleItinerary = props?.templateKeys?.filter(schema => schema.enrichmentProduct === 'MULTIPLE_ITINERARIES').length > 0;

  const classes = useStyles();
  const store = useStore();
  const { refreshSessionIfNeeded } = sessionHelper(store);
  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [editorStaticContent, setEditorStaticContent] = useState(null);

  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );
  const currentTemplateLanguage = useSelector(store => store.settings.currentTemplateLanguage);
  const selectedTenant = useSelector(store => store.user?.selectedTenant || {});
  const selectedBusinessContext = useSelector(store => store.user?.selectedBusinessContext || {});
  const uploadAssets = useSelector(store => store.uploads?.list || []);
  const dateFormatMenuOptions = dateFormatFunction(settingsMetaDataStore, editorStaticContent);

  const [hasLoaded, setHasLoaded] = useState(false);
  const [triggerSave, setTriggerSave] = useState(false);
  const [toolBarWidth, setToolBarWidth] = useState(TOOLBAR_LARGE_WIDTH);
  const [editorWidth, setEditorWidth] = useState(EDITOR_LARGE_WIDTH);
  const [previewEditorWidth, setPreviewEditorWidth] = useState(
    PREVIEW_EDITOR_LARGE_WIDTH
  );
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [keysOpen, setKeysOpen] = useState(false);
  const [dataFrameKeys, setDataFrameKeys] = useState({});
  const [pageLayoutOpen, setPageLayoutOpen] = useState(false);
  const [uploadsOpen, setUploadsOpen] = useState(false);

  const [currentCopiedValue, setCurrentCopiedValue] = useState('');
  const dispatch = useDispatch();

  const emailEditorRef = useRef(null);
  const emailWrapperRef = useRef({});
  const uploadAssetsRef = useRef(uploadAssets);
  const hasRegisteredUploadsProviderRef = useRef(false);
  const hasRegisteredCallbacksRef = useRef(false);
  const selectedTenantRef = useRef(selectedTenant);
  const selectedBusinessContextRef = useRef(selectedBusinessContext);
  const pendingUnlayerDeleteRef = useRef(null);
  const [showUnlayerDeleteConfirm, setShowUnlayerDeleteConfirm] = useState(false);

  useEffect(() => {
    dispatch({ type: uploadActions.GET_UPLOADS });
  }, [dispatch]);

  useEffect(() => {
    uploadAssetsRef.current = uploadAssets;
  }, [uploadAssets]);

  useEffect(() => {
    selectedTenantRef.current = selectedTenant;
  }, [selectedTenant]);

  useEffect(() => {
    selectedBusinessContextRef.current = selectedBusinessContext;
  }, [selectedBusinessContext]);


  useImperativeHandle(ref, () => ({
    exportAndSaveTemplate(event) {
      const { current } = emailEditorRef;
      if (current && typeof current.exportHtml === 'function') {
        current.exportHtml(data => {
          // Check if sanitization is enabled via environment config
          const isSanitizationEnabled = getEnvVar('ADM_ENABLE_HTML_SANITIZATION') === 'true';
          
          const sanitizedHtml = isSanitizationEnabled 
            ? sanitizeHtmlWithHandlebarPreservation(data.html)
            : data.html;
          const sanitizedDesign = isSanitizationEnabled
            ? sanitizeData(data.design)
            : data.design;
          
          data = { ...data, html: sanitizedHtml, design: sanitizedDesign };
          let missingLink = updateSubscribe(
            event,
            thisTemplate,
            data,
            useUnsubscribeLinkOption,
            openSnackBarError,
            editorStaticContent
          );
          if (missingLink == 1) return;
          handleChange(
            [
              {
                target: {
                  name: 'includesSurveyLink',
                  value: isSurveyLinkIncluded(data)
                }
              },
              {
                target: {
                  name: 'jsonContent',
                  value: data
                }
              },
              {
                target: {
                  name: 'content',
                  value: data.html
                }
              }
            ],
            event?.type === 'customizeTemplate'
          );
          testEventType(event, validateTestTemplate, setTriggerSave);
        });
      } else {
        if (event?.type === 'testTemplate') {
          validateTestTemplate();
        } else if (event?.type !== 'customizeTemplate') {
          setTriggerSave(true);
        }
      }
    },
    /**
     * Export the current editor content for testing without saving
     * @param {Function} callback - Called with the exported jsonContent data
     */
    exportForTest(callback) {
      const { current } = emailEditorRef;
      if (current && typeof current.exportHtml === 'function') {
        current.exportHtml(data => {
          // FIX: Use same sanitization as save to preserve handlebars and document structure
          const isSanitizationEnabled = getEnvVar('ADM_ENABLE_HTML_SANITIZATION') === 'true';
          
          const sanitizedHtml = isSanitizationEnabled 
            ? sanitizeHtmlWithHandlebarPreservation(data.html)
            : data.html;
          const sanitizedDesign = isSanitizationEnabled
            ? sanitizeData(data.design)
            : data.design;
          
          const exportedData = { ...data, html: sanitizedHtml, design: sanitizedDesign };
          callback(exportedData);
        });
      } else {
        console.error('current is null or exportHtml is not a function');
        callback(null);
      }
    }
  }));



  const handleSnackBarClose = () => {
    setSnackBarInfo({ show: false });
  };

  const openSnackBarError = (severity, message) => {
    setSnackBarInfo({
      show: true,
      severity,
      message
    });
  };

  useEffect(() => {
    if (templateLanguageContent) {
      let unlayerEditorContent = getTemplateLanguageContent("unlayerEditor");
      let customizedContent = {
        "editor.image.drop_upload": unlayerEditorContent?.["editor.image.drop_upload"].replace("{{acceptTypesLabel}}", acceptTypesLabel).replace("{{acceptLimitLabel}}", acceptLimitLabel)
      };
      setEditorStaticContent({ ...unlayerEditorContent, ...customizedContent });
    }
  }, [templateLanguageContent]);

  useEffect(() => {
    if (currentCopiedValue !== '') {
      navigator.clipboard.writeText(currentCopiedValue);
    }
  }, [currentCopiedValue]);

  useEffect(() => {
    let width = get(emailWrapperRef, 'current.offsetWidth');
    const newWidths = calculateWidths(width);
    if (Object.keys(newWidths).length > 0) {
      setToolBarWidth(newWidths.toolBarWidth);
      setPreviewEditorWidth(newWidths.previewEditorWidth);
      setEditorWidth(newWidths.editorWidth);
    }
  }, [windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (props.templateKeys?.length > 0) {
      let temp = filterDataFrame(props.templateKeys, thisTemplate);
      setDataFrameKeys({ ...temp });
    }
  }, [props.templateKeys, thisTemplate.schemaIdentifier]);

  const loadTemplate = () => {
    if (
      hasLoaded &&
      thisTemplate.jsonContent &&
      thisTemplate.jsonContent.design &&
      (editorStaticContent || bypassLanguageCheck)
    ) {
      const { current } = emailEditorRef;
      
      if (currentTemplateLanguage?.unlayerLocale && templateLanguageContent && current?.editor && editorStaticContent) {
        current.editor.setLocale(currentTemplateLanguage?.unlayerLocale);
        current.editor.setTranslations({
          [currentTemplateLanguage?.unlayerLocale]: editorStaticContent
        });
      }
      current.loadDesign(thisTemplate.jsonContent.design);
      current.addEventListener('design:updated', data => {
        validateIteration(current, setIsValidationError);
        updateValidationError(
          data,
          setIsValidationError,
          refreshSessionIfNeeded
        );
      }
      );
    }
  };

  // Only trigger save when triggerSave state changes to true.
  // We don't include saveTemplate in dependencies because including it would cause
  // this effect to re-run on every parent render (since saveTemplate is recreated),
  // leading to infinite save loops and 409 conflict errors on the backend.
  useEffect(() => {
    triggerSaveOnCondition(
      triggerSave,
      shouldAutoSave,
      autoSaveTemplate,
      saveTemplate,
      setTriggerSave
    );
  }, [triggerSave]);

  useEffect(loadTemplate, [thisTemplate.jsonContent, hasLoaded, editorStaticContent, bypassLanguageCheck]);

  const getAzureApiRoot = () => {
    const apiBase =
      (window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
      process.env.REACT_APP_USER_API_ROOT ||
      '';
    const tenantId =
      selectedTenantRef.current?.tenantId ||
      window.localStorage.getItem('GCM-tenantId');
    const propertyId =
      selectedBusinessContextRef.current?.businessContextId ||
      window.localStorage.getItem('GCM-businessContextId') ||
      'Default';
    return `${apiBase}/content-service/content/adm/v1/tenants/${tenantId}/properties/${propertyId}/azure`;
  };

  const returnFileSize = number => {
    if (number < 1024) {
      return `${number} bytes`;
    } else if (number >= 1024 && number < 1048576) {
      return `${(number / 1024).toFixed(1)} KB`;
    } else if (number >= 1048576) {
      return `${(number / 1048576).toFixed(1)} MB`;
    }
  }

  const registerCDNUploadHandler = () => {
    try {
      const { current } = emailEditorRef;
      const editorApi = current?.editor;
      const globalUnlayer = window?.unlayer;
      const target = editorApi || globalUnlayer;

      if (!target || typeof target.registerCallback !== 'function') {
        console.warn('Unlayer registerCallback not available');
        return;
      }

      target.registerCallback('image', function(file, done) {
        // Capture the current image src before uploading so we can restore it on failure.
        // Unlayer has no error state in done() — only done({ progress:100, url }) clears
        // the uploading spinner, so we always call done with progress:100 and either the
        // new CDN url (success) or the original src (failure) to dismiss the progress bar.
        // When originalSrc is empty (new image block), fall back to a 1×1 transparent PNG
        // so Unlayer receives a valid URL and doesn't show "No image url received".
        let unlayerPlaceholder = getEnvVar('ADM_IMAGE_PLACEHOLDER') ? getEnvVar('ADM_IMAGE_PLACEHOLDER') : 'https://cdn.tools.unlayer.com/image/placeholder.png';
        const UNLAYER_PLACEHOLDER = unlayerPlaceholder;
        const selectedComponent = typeof target.getSelected === 'function' ? target.getSelected() : null;
        const rawSrc = selectedComponent?.get?.('src') || selectedComponent?.getAttributes?.()?.src || '';
        const fallbackUrl = rawSrc || UNLAYER_PLACEHOLDER;

        const dismissWithError = (message) => {
          openSnackBarError('error', message);
          // Pass a valid url so Unlayer clears its uploading state
          setTimeout(() => {
            done({ progress: 100, url: fallbackUrl });
          }, 0);
        };

        if (!file || !file.attachments || file.attachments.length === 0) {
          dismissWithError(`${editorStaticContent?.no_file_selected_for_upload || 'Upload failed: No file selected.' }`);
          return;
        }

        const rawFile = file.attachments[0];

        if (acceptTypes.indexOf(rawFile.type) < 0) {
          dismissWithError(`${rawFile?.name} ${editorStaticContent?.dataframe_error_image_size[0] || `is not supported`}. ${acceptTypesLabel}`);
          return;
        }
        if (rawFile.size > acceptLimit) {
          dismissWithError(`${editorStaticContent?.dataframe_error_image_format[0] || `Size of`} ${rawFile?.name} ${editorStaticContent?.dataframe_error_image_format[1] || `is`} ${returnFileSize(rawFile?.size)}. ${editorStaticContent?.dataframe_error_image_format[2] || `Maximum allowed size is`} ${acceptLimitLabel} MB`);
          return;
        }

        const originalName = rawFile.name || 'image';
        const isEditedImage = /^edited-image/i.test(originalName);
        let uploadName = originalName;
        if (isEditedImage) {
          const dotIndex = originalName.lastIndexOf('.');
          uploadName = dotIndex !== -1
            ? `${originalName.slice(0, dotIndex)}-${Date.now()}${originalName.slice(dotIndex)}`
            : `${originalName}-${Date.now()}`;
        }
        const fileToUpload = isEditedImage
          ? new File([rawFile], uploadName, { type: rawFile.type })
          : rawFile;
        const formData = new FormData();
        formData.append('file', fileToUpload);

        const uploadEndpoint = getAzureApiRoot();
        const customTag = getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm';
        axios.post(uploadEndpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            customTag: customTag
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              if (progress < 100) {
                done({ progress });
              }
            }
          }
        })
          .then(response => {
            const data = response.data;
            const cdnUrl = data.cdnPublicUrl || data.publicUrl || data.filelink || data.url;
            if (!cdnUrl) {
              throw new Error('No URL in upload response');
            }
            // Optimistically prepend the new asset so the provider returns it
            // immediately when Unlayer re-queries after done() is called.
            // The subsequent GET_UPLOADS will replace the ref with the full
            // server list (which also includes this asset), so no duplicate remains.
            uploadAssetsRef.current = [data, ...uploadAssetsRef.current];
            dispatch({ type: uploadActions.GET_UPLOADS });
            done({ progress: 100, url: cdnUrl });
          })
          .catch(error => {
            const responseData = error?.response?.data;
            const isNetworkError = !error?.response && error?.message === 'Network Error';
            const isDuplicate = error?.response?.status === 409 || responseData?.status === 409 || responseData?.code === 4002;
            const isBadRequest = error?.response?.status === 400 || responseData?.status === 400 || responseData?.code === 4001;
          
            const message = isNetworkError
              ? (editorStaticContent?.network_error ||
                  'Image upload failed: Network Error. Please try again.')
              : isDuplicate
              ? (editorStaticContent?.image_duplicate_error ||
                  'Image upload failed: A file with the same name already exists. Please rename the file and try again.')
              : isBadRequest
              ? (editorStaticContent?.bad_request_error ||
                  'Image upload failed: Invalid file format or size. Please check the file and try again.')
              : (editorStaticContent?.image_upload_error ||
                  'Image upload failed: Unknown error occurred.');

            dismissWithError(`${message}`);
          });
      });
    } catch (error) {
      console.error('Error registering CDN upload handler:', error);
    }
  };

  const registerImageRemovalHandler = () => {
    try {
      const { current } = emailEditorRef;
      const editorApi = current?.editor;
      const globalUnlayer = window?.unlayer;
      const target = editorApi || globalUnlayer;

      if (!target || typeof target.registerCallback !== 'function') {
        console.warn('Unlayer registerCallback not available');
        return;
      }

      // NOTE: This callback fires only for images served via registerProvider('userUploads').
      // Images stored in Unlayer's own cloud (uploaded before this handler was active) will
      // always call api.unlayer.com/v2/images DELETE directly — that is expected Unlayer behaviour.
      target.registerCallback('image:removed', function(image, done) {
        if (!image || !image.id) {
          console.warn('image:removed — missing image id', image);
          done();
          return;
        }
        // Store the pending deletion and show a confirmation dialog.
        // If the user cancels, done() is still called so Unlayer unblocks its UI,
        // but the image is not deleted from the CDN — it will reappear on next provider refresh.
        pendingUnlayerDeleteRef.current = { image, done };
        setShowUnlayerDeleteConfirm(true);
      });
    } catch (error) {
      console.error('Error registering image removal handler:', error);
    }
  };

  const handleUnlayerDeleteConfirm = () => {
    const pending = pendingUnlayerDeleteRef.current;
    setShowUnlayerDeleteConfirm(false);
    pendingUnlayerDeleteRef.current = null;
    if (!pending) return;
    const { image, done } = pending;
    const deleteEndpoint = `${getAzureApiRoot()}/${encodeURIComponent(image.id)}`;
    axios.delete(deleteEndpoint)
      .then(() => {
        dispatch({ type: uploadActions.GET_UPLOADS });
        openSnackBarError('success', `${editorStaticContent?.deleted_image_success || `Image deleted successfully.`}`);  
        done();
      })
      .catch(error => {
        const message = error?.response?.data?.message || error.message;
        console.error(`Failed to delete image ${image.id}:`, message);
        openSnackBarError('error', `${editorStaticContent?.deleted_image_error || `Failed to delete image:`} ${message}`);
        done();
      });
  };

  const handleUnlayerDeleteCancel = () => {
    const pending = pendingUnlayerDeleteRef.current;
    setShowUnlayerDeleteConfirm(false);
    pendingUnlayerDeleteRef.current = null;
    if (!pending) return;
    // Call done() without deleting — Unlayer re-queries the provider and the image reappears.
    pending.done();
    dispatch({ type: uploadActions.GET_UPLOADS });
  };

  const handleDeleteUpload = async asset => {
    const uploadId = asset?.id || asset?.assetId;
    if (!uploadId) return;
    try {
      await axios.delete(`${getAzureApiRoot()}/${encodeURIComponent(String(uploadId))}`);
      dispatch({ type: uploadActions.GET_UPLOADS });
      openSnackBarError('success', `${editorStaticContent?.deleted_image_success || `Image deleted successfully.`}`);
    } catch (error) {
      openSnackBarError('error', error?.response?.data?.message || `${editorStaticContent?.deleted_image_error || `Failed to delete image.`}`);
    }
  };

  const registerUploadsProvider = () => {
    if (hasRegisteredUploadsProviderRef.current) {
      return;
    }

    const providerHandler = (params, done) => {
      const { images, pagination } = getUnlayerUploadsResponse(
        uploadAssetsRef.current,
        params
      );
      done(images, pagination);
    };

    const { current } = emailEditorRef;
    const editorApi = current?.editor;
    const globalUnlayer = window?.unlayer;

    if (typeof editorApi?.registerProvider === 'function') {
      editorApi.registerProvider('userUploads', providerHandler);
      hasRegisteredUploadsProviderRef.current = true;
      return;
    }

    if (typeof globalUnlayer?.registerProvider === 'function') {
      globalUnlayer.registerProvider('userUploads', providerHandler);
      hasRegisteredUploadsProviderRef.current = true;
    }
  };

  const onLoad = () => {
    setHasLoaded(true);
  };

  const onReady = () => {
    registerUploadsProvider();
    
    // Register CDN callbacks only once to avoid duplicate handlers
    if (!hasRegisteredCallbacksRef.current) {
      registerCDNUploadHandler();
      registerImageRemovalHandler();
      hasRegisteredCallbacksRef.current = true;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid container className={classes.editorBar} ref={emailWrapperRef}>
        <Box className={classes.subjectLine}>{children}</Box>
        {!isTemplateReadOnly(isPopupDialog) && (
          <Box style={{ width: toolBarWidth }} className={classes.tools}>
            {hasLoaded && (
              <AppBar
                position="static"
                className={classes.appBar}
                color="default"
                elevation={0}>
                <Toolbar className={classes.toolbar}>
                  <IconButton
                    onClick={() => {
                      setKeysOpen(!keysOpen);
                      setPageLayoutOpen(false);
                      setUploadsOpen(false);
                    }}
                    edge="start"
                    classes={{ label: classes.iconButtonLabel }}
                    color="inherit"
                    aria-label="menu"
                    label="Template Keys">
                    {keysOpen 
                        ? <Code color="primary"/>
                        : <Code />
                      }
                    <Typography variant="button" display="block" gutterBottom>
                      {editorStaticContent?.templateKeys || `Template Keys`}
                    </Typography>
                  </IconButton>
                  {(getNestedDataField(dataFrameKeys).map(nestedDataFields => nestedDataFields.dataFrames).concat().flat()).length > 0 && (
                    <IconButton
                      onClick={() => {
                        setPageLayoutOpen(!pageLayoutOpen);
                        setKeysOpen(false);
                        setUploadsOpen(false);
                      }}
                      edge="start"
                      classes={{ label: classes.iconButtonLabel }}
                      color="inherit"
                      aria-label="menu"
                      label="Multipage Layout">
                        {pageLayoutOpen 
                          ? <Queue color="primary"/>
                          : <Queue />
                        }
                      <Typography variant="button" display="block" gutterBottom>
                        {editorStaticContent?.multipageLayout || `Multipage Layout`}
                      </Typography>
                    </IconButton>
                  )}
                </Toolbar>
              </AppBar>
            )}
          </Box>
        )}
      </Grid>
      {Object.getOwnPropertyNames(dataFrameKeys).length > 0 && (
        <Grid item xs={12} className={classes.editorWrapper}>
          {isTemplateReadOnly(isPopupDialog) ? (
            <React.Fragment>
              <div
                className={classes.overlayToolContainer}
                style={{ width: toolBarWidth }}>
                <Collapse in={true} style={{ width: toolBarWidth }}></Collapse>
              </div>
              <div
                className={classes.previewEditorContainer}
                style={{ width: previewEditorWidth }}>
                <Collapse
                  in={true}
                  style={{ width: previewEditorWidth }}></Collapse>
              </div>
              <div
                className={classes.editorContainer}
                style={{ width: editorWidth }}>
                <Collapse in={true} style={{ width: editorWidth }}></Collapse>
              </div>
            </React.Fragment>
          ) : (
            <>
              <div
                className={classes.toolContainer}
                style={{
                  width: toolBarWidth,
                  visibility: keysOpen ? 'visible' : 'hidden'
                }}>
                <Collapse
                  in={keysOpen}
                  style={{
                    width: toolBarWidth
                  }}>
                  <Box elevation={4} className={classes.templateKeysPanel}>
                    <TemplateKeyBank
                      isOpen={keysOpen}
                      templateKeys={templateKeys(
                        props.templateKeys,
                        useUnsubscribeLinkOption,
                        useSurveysLinkOption,
                        surveys,
                        thisTemplate,
                        editorStaticContent
                      ) || []}
                    />
                  </Box>
                </Collapse>
              </div>
              <div
                className={classes.toolContainer}
                style={{
                  width: toolBarWidth,
                  visibility: pageLayoutOpen ? 'visible' : 'hidden'
                }}>
                <Collapse
                  in={pageLayoutOpen}
                  style={{
                    width: toolBarWidth
                  }}>
                  <Box elevation={4} className={classes.sideKeysPanel}>
                    <PageLayout 
                      pageIterators={getNestedDataField(dataFrameKeys)}
                      pageLayout={thisTemplate.pageIterator}
                      setPageLayout={modifyPageIterator}
                    />
                  </Box>
                </Collapse>
              </div>
              <div
                className={classes.toolContainer}
                style={{
                  width: toolBarWidth,
                  visibility: uploadsOpen ? 'visible' : 'hidden'
                }}>
                <Collapse
                  in={uploadsOpen}
                  style={{
                    width: toolBarWidth
                  }}>
                  <Box elevation={4} className={classes.templateKeysPanel}>
                    <UploadsPanel
                      isOpen={uploadsOpen}
                      uploads={uploadAssets}
                      onDeleteImage={handleDeleteUpload}
                    />
                  </Box>
                </Collapse>
              </div>
            </>
          )}
          {(thisTemplate.productType === ProductType.List ||
            (thisTemplate.schemaIdentifier && dataFrameKeys.hasList)) &&
            (editorStaticContent || bypassLanguageCheck) &&
            (isTemplateReadOnly(isPopupDialog) ? (
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onLoad}
                onReady={onReady}
                name="jsonContent"
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                projectId={
                  parseInt(getEnvVar('UNLAYER_PROJECT_ID')) ||
                  UnlayerProjectIds.DefaultId
                }
                options={{
                  customJS: [
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_dataframe_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_qr_code_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_bar_code_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_iteration_unlayer.js'
                  ],
                  user: {
                    id: String(selectedTenant?.tenantId || thisTemplate?.tenantId || 'default')
                  },
                  features: {
                    undoRedo: false,
                    preview: false,
                    userUploads: {
                      enabled: true
                    }
                  },
                  appearance: {
                    panels: {
                      tools: { collapsible: false }
                    }
                  },
                  tabs: {
                    content: { enabled: false },
                    blocks: { enabled: false },
                    body: { enabled: false },
                    images: { enabled: false }
                  },
                  tools: {
                    image: { enabled: false },
                    columns: { enabled: false },
                    button: { enabled: false },
                    divider: { enabled: false },
                    html: { enabled: false },
                    heading: { enabled: false },
                    menu: { enabled: false },
                    text: { enabled: false },
                    'custom#DataFrame': { enabled: false },
                    'custom#QrCode': { enabled: false },
                    'custom#Barcode': { enabled: false },
                    'custom#Iteration': { enabled: false }
                  }
                }}
              />
            ) : (
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onLoad}
                onReady={onReady}
                name="jsonContent"
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                projectId={
                  parseInt(getEnvVar('UNLAYER_PROJECT_ID')) ||
                  UnlayerProjectIds.DefaultId
                }
                options={{
                  customJS: [
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_dataframe_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_qr_code_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_bar_code_unlayer.js',
                    window.location.protocol +
                    '//' +
                    window.location.host +
                    '/gcm/editor_components/custom_iteration_unlayer.js'
                  ],
                  user: {
                    id: String(selectedTenant?.tenantId || thisTemplate?.tenantId || 'default')
                  },
                  features: {
                    userUploads: {
                      enabled: true
                    }
                  },
                  tools: {
                    image: {
                      properties: {
                        src: {
                          value: {
                            url: imagePlaceholder,
                          },
                        },
                      },
                    },
                    'custom#DataFrame': {
                      data: {
                        dataFields: getDataField(dataFrameKeys),
                        translations: editorStaticContent
                      },
                      properties: {
                        menu: {
                          editor: {
                            data: getData(dataFrameKeys, dateFormatMenuOptions, openSnackBarError, editorStaticContent, setCurrentCopiedValue, hasMultipleItinerary)
                          }
                        }
                      }
                    },
                    'custom#QrCode': {
                      data: {
                        translations: editorStaticContent
                      },
                      properties: {
                        menu: {
                          editor: {
                            data: {
                              translations: editorStaticContent,
                              templateKeys: templateKeys(props.templateKeys,
                                false,
                                false,
                                surveys,
                                thisTemplate,
                                editorStaticContent) || [],
                              customQrCodeLength: parseInt(getEnvVar('ADM_MAX_QR_LENGTH')) || 100,
                              openSnackBarError: openSnackBarError
                            }
                          }
                        }
                      }
                    },
                    'custom#Barcode': {
                      data: {
                        translations: editorStaticContent
                      },
                      properties: {
                        menu: {
                          editor: {
                            data: {
                              translations: editorStaticContent,
                              templateKeys: templateKeys(props.templateKeys,
                                false,
                                false,
                                surveys,
                                thisTemplate,
                                editorStaticContent) || [],
                              openSnackBarError: openSnackBarError
                            }
                          }
                        }
                      }
                    },
                    'custom#Iteration': {
                      enabled: (getNestedDataField(dataFrameKeys).map(nestedDataFields => nestedDataFields.dataFrames).concat().flat()).length > 0,
                      data: {
                        dataFields: getNestedDataField(dataFrameKeys),
                        translations: editorStaticContent
                      },
                      properties: {
                        menu: {
                          editor: {
                            data: getData(dataFrameKeys, dateFormatMenuOptions, openSnackBarError, editorStaticContent)
                          }
                        }
                      }
                    }
                  }
                }}
              />
            ))}{' '}
          {thisTemplate.productType !== ProductType.List &&
            thisTemplate.schemaIdentifier &&
            !dataFrameKeys.hasList && (
              <EmailEditor
                ref={emailEditorRef}
                projectId={
                  parseInt(getEnvVar('UNLAYER_PROJECT_ID')) ||
                  UnlayerProjectIds.DefaultId
                }
                onLoad={onLoad}
                onReady={onReady}
                name="jsonContent"
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                options={{
                  user: {
                    id: String(selectedTenant?.tenantId || thisTemplate?.tenantId || 'default')
                  },
                  features: {
                    userUploads: {
                      enabled: true
                    }
                  }
                }}
              />
            )}
        </Grid>
      )}
      <Snackbar
        open={snackbarInfo.show}
        onClose={handleSnackBarClose}
        autoHideDuration={3000}>
        <Alert show={snackbarInfo.show} severity={snackbarInfo.severity}>
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
      <PopupModal
        isOpen={showUnlayerDeleteConfirm}
        handleClose={handleUnlayerDeleteCancel}
        title={editorStaticContent?.uploads_panel_delete_title || 'Delete Image'}
        severity="warning"
        content={editorStaticContent?.deleteImageConfirmation || 'Are you sure you want to delete this image? This action will remove it from both Template Uploads and Uploads page'}
        buttonGroup={[
          { title: editorStaticContent?.uploads_panel_delete_btn || 'Delete', action: handleUnlayerDeleteConfirm },
          { title: editorStaticContent?.uploads_panel_cancel_btn || 'Cancel', action: handleUnlayerDeleteCancel }
        ]}
      />
    </Grid>
  );
});

export default TemplateEditor;
